import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useActiveLeague } from "../../hooks/useActiveLeague";
import { useCurrentTeamId } from "../../hooks/useCurrentTeamId";
import { buildTeamLink, buildPublicTeamLink } from "../../utils/leagueLinks";
import { useGetLeagueFixtures } from "../../api/fixturesApi";
import "../../styles/matches.css";
import { toast } from 'react-toastify';
import { formatISO } from 'date-fns'; // Import formatISO
import ballIcon from '../../assets/messages/ball.png'; // Import ball icon
import { supabase } from '../../lib/supabaseClient'; // Import Supabase client
import { useSocket } from '../../context/SocketContext.jsx';
import {
    getFinalScoreFromCompletionPayload,
    getMatchIdFromSocketPayload
} from '../../utils/matchCompletion';
import TutorialTooltip from '../common/TutorialTooltip';
import { Spinner } from "../common/Spinner";

// const BACKEND_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'; // No longer needed for relative paths
const FRONTEND_FALLBACK_LOGO_ASSET = ballIcon; // Path to the frontend asset (e.g., /assets/ball-B938cI2Y.png after build)
const BACKEND_DEFAULT_TEAM_LOGO_PATH = '/uploads/teams/7b9a405a6a953b17e04a8f0107c3e5fd.jpg'; // Canonical backend default

// Helper function to clean up and validate logo URLs
const getCleanLogoUrl = (logoUrl) => {
    if (!logoUrl || logoUrl.trim() === "" || logoUrl === "/uploads/teams/default-team-logo.png") {
        // If logoUrl is empty, null, or the specific placeholder, use the canonical backend default
        return BACKEND_DEFAULT_TEAM_LOGO_PATH;
    }
    
    // If it's already our fallback, return as-is
    if (logoUrl === FRONTEND_FALLBACK_LOGO_ASSET) { // Check against the frontend asset
        return logoUrl;
    }
    
    // Handle malformed URLs that start with /https:// or /http://
    if (logoUrl.startsWith('/https://') || logoUrl.startsWith('/http://')) {
        return logoUrl.substring(1); // Remove the leading slash
    }
    
    // Handle regular absolute URLs
    if (logoUrl.startsWith('https://') || logoUrl.startsWith('http://')) {
        return logoUrl;
    }
    
    // Handle valid relative paths from backend (these should be served by the backend proxy)
    if (logoUrl.startsWith('/uploads/')) {
        return logoUrl;
    }
    
    console.warn(`[getCleanLogoUrl] Unrecognized logo URL format: "${logoUrl}". Falling back to backend default.`);
    return BACKEND_DEFAULT_TEAM_LOGO_PATH;
};

// Normalise status values to a compact lowercase form for robust comparisons
const normalizeStatusValue = (status) => {
    if (!status) return '';
    return status.toString().trim().toLowerCase().replace(/_/g, '');
};

// Helper function to extract file path from Supabase URL
const extractSupabaseFilePath = (url) => {
    if (!url || !url.includes('supabase.co')) return null;
    
    // Extract path from URLs like: https://xsrwligqdlmkyqdsczru.supabase.co/storage/v1/object/public/team-logos/67e9571ff02fc4024095e886/logo.jpg
    const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
    return match ? match[1] : null;
};

// Component for handling individual team logo with error tracking and authenticated loading
const TeamLogo = ({ logoUrl, teamName, className = "" }) => {
    const [currentSrc, setCurrentSrc] = useState(FRONTEND_FALLBACK_LOGO_ASSET); // Initial state before useEffect
    const [hasErrored, setHasErrored] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const errorCountRef = useRef(0);
    const blobUrlRef = useRef(null);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => { // Check against FRONTEND_FALLBACK_LOGO_ASSET
            if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrlRef.current);
            }
        };
    }, []);

    // Load image when logoUrl changes
    useEffect(() => {
        setHasErrored(false);
        errorCountRef.current = 0;
        setIsLoading(false);

        const loadImage = async () => {
            if (!logoUrl) {
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            const cleanUrl = getCleanLogoUrl(logoUrl);
            
            // If it's not a Supabase URL, use it directly
            if (!cleanUrl.includes('supabase.co')) {
                setCurrentSrc(cleanUrl);
                return;
            }

            // For Supabase URLs, use authenticated download
            const filePath = extractSupabaseFilePath(cleanUrl);
            if (!filePath) {
                console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            setIsLoading(true);
            
            try {
                // Determine bucket from file path
                const [bucketName, ...pathParts] = filePath.split('/');
                const fileName = pathParts.join('/');
                
                const { data, error } = await supabase.storage
                    .from(bucketName)
                    .download(fileName);
                
                if (error) {
                    throw error;
                }
                
                // Cleanup previous blob URL
                if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }
                
                // Create new blob URL
                const blobUrl = URL.createObjectURL(data);
                blobUrlRef.current = blobUrl;
                setCurrentSrc(blobUrl);
                setIsLoading(false);
                
            } catch (error) {
                console.log(`Error loading ${teamName} logo from Supabase:`, error.message);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                setHasErrored(true);
                setIsLoading(false);
            }
        };

        loadImage();
    }, [logoUrl, teamName]);

    const handleError = (e) => {
        errorCountRef.current += 1;
        
        // Prevent infinite loops - max 1 error attempt for authenticated loading
        if (errorCountRef.current > 1 || hasErrored) {
            console.warn(`Too many errors for ${teamName} logo, stopping retries`);
            return;
        }

        console.log(`Error loading ${teamName} logo (attempt ${errorCountRef.current}):`, e.target.src);
        
        // Set fallback and mark as errored
        setHasErrored(true);
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
    };

    // Show loading state for Supabase images
    if (isLoading) {
        return (
            <img 
                src={FRONTEND_FALLBACK_LOGO_ASSET} 
                alt={`Loading ${teamName || 'team'} logo...`} 
                className={`${className} loading-opacity`}
                style={{ opacity: 0.5 }}
            />
        );
    }

    return (
        <img 
            src={currentSrc} 
            alt={teamName || 'Team logo'} 
            className={className}
            onError={handleError}
        />
    );
};

function MatchesBlock({ leagueId, selectedDate }) {
  const { activeLeague } = useActiveLeague();
  const currentTeamId = useCurrentTeamId();
    const socketCtx = useSocket();
    const [liveScores, setLiveScores] = useState({}); // { [matchId]: { home, away } }
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 10;

    // Format the selectedDate to YYYY-MM-DD for the API query
    const formattedDateQuery = selectedDate ? formatISO(selectedDate, { representation: 'date' }) : undefined;

    // Pass formattedDateQuery to the hook
    const { data: fixturesData, isLoading, error, refetch } = useGetLeagueFixtures(
        leagueId,
        currentPage,
        limit,
        formattedDateQuery // Add date query parameter
    );

    // Refetch when page, leagueId, or selectedDate changes
    useEffect(() => {
        if (leagueId) {
            console.log(`Refetching fixtures for league ${leagueId}, date: ${formattedDateQuery}, page: ${currentPage}`);
            refetch();
        }
    }, [currentPage, leagueId, selectedDate, refetch, formattedDateQuery]); // Add selectedDate and formattedDateQuery to dependencies

    // Add logging for debugging
    useEffect(() => {
        console.log('Fixtures data received:', fixturesData);
        if (fixturesData?.matches?.length > 0) {
            fixturesData.matches.forEach(match => {
                if (match.homeTeam) {
                    console.log('Home team logo (raw):', match.homeTeam.logo);
                    console.log('Home team logo (clean):', getCleanLogoUrl(match.homeTeam.logo));
                }
                if (match.awayTeam) {
                    console.log('Away team logo (raw):', match.awayTeam.logo);
                    console.log('Away team logo (clean):', getCleanLogoUrl(match.awayTeam.logo));
                }
            });
        }
    }, [fixturesData]);

    // Subscribe to live commentary updates to keep scores fresh for visible fixtures
    useEffect(() => {
        if (!socketCtx || !fixturesData?.matches) return;
        const visibleIds = new Set(fixturesData.matches.map(m => m?._id).filter(Boolean));

        const unsubCommentary = socketCtx.subscribe('commentary_update', (payload) => {
            try {
                const matchId = getMatchIdFromSocketPayload(payload);
                const score = payload?.score;
                if (!matchId || !score) return;
                if (!visibleIds.has(matchId)) return; // only update rows we are showing

                setLiveScores(prev => {
                    const prevScore = prev[matchId];
                    if (prevScore && prevScore.home === score.home && prevScore.away === score.away) return prev;
                    return { ...prev, [matchId]: { home: score.home, away: score.away } };
                });
            } catch (e) {
                console.warn('[Fixtures live score] Failed to process commentary_update payload:', e);
            }
        });

        const applyCompletionScore = (payload, eventName) => {
            try {
                const matchId = getMatchIdFromSocketPayload(payload);
                const finalScore = getFinalScoreFromCompletionPayload(payload);
                if (!matchId || !finalScore) return;
                if (!visibleIds.has(matchId)) return;
                setLiveScores(prev => ({
                    ...prev,
                    [matchId]: { home: finalScore.home, away: finalScore.away }
                }));
            } catch (e) {
                console.warn(`[Fixtures live score] Failed to process ${eventName} payload:`, e);
            }
        };

        const unsubSimEnded = socketCtx.subscribe('simulation_ended', (payload) => {
            applyCompletionScore(payload, 'simulation_ended');
        });

        const unsubMatchCompleted = socketCtx.subscribe('match_completed', (payload) => {
            applyCompletionScore(payload, 'match_completed');
        });

        return () => {
            if (typeof unsubCommentary === 'function') unsubCommentary();
            if (typeof unsubSimEnded === 'function') unsubSimEnded();
            if (typeof unsubMatchCompleted === 'function') unsubMatchCompleted();
        };
    }, [socketCtx, fixturesData?.matches]);

    const leagueMatches = fixturesData?.matches?.map(function (match) {
        const matchTime = match?.date;
        const date = new Date(matchTime);
        const formattedTime = date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
        });

        // Clean and validate team logo URLs
        const homeLogoUrl = getCleanLogoUrl(match?.homeTeam?.logo);
        const awayLogoUrl = getCleanLogoUrl(match?.awayTeam?.logo);

        return (
            <div key={match._id} className="match-row">
                <div className="time">
                    {(() => {
                        const norm = normalizeStatusValue(match.status);
                        const isLive = norm === 'inprogress';
                        const isCompleted = norm === 'completed' || norm === 'forfeited';
                        if (isLive) {
                            return (
                                <span className="live-indicator" aria-label="Live">
                                    <span className="dot" />
                                    LIVE
                                </span>
                            );
                        }
                        if (isCompleted) {
                            return 'Full Time';
                        }
                        return formattedTime;
                    })()}
                </div>
                <div className="teams-block">
                    <div className="team">
                        <Link to={activeLeague ? (match?.homeTeam?._id === currentTeamId ? buildTeamLink(match?.homeTeam?._id, `/${activeLeague.slug}`) : buildPublicTeamLink(match?.homeTeam?._id, `/${activeLeague.slug}`)) : '#'}>{match?.homeTeam?.name}</Link>
                        <div className="image">
                            <TeamLogo 
                                logoUrl={homeLogoUrl} 
                                teamName={match?.homeTeam?.name}
                            />
                        </div>
                    </div>
                    { (() => {
                        const norm = normalizeStatusValue(match.status);
                        const isCompleted = norm === 'completed';
                        const isLive = norm === 'inprogress';
                        const overrideScore = liveScores[match._id];
                        const homeScore = overrideScore?.home ?? match.homeScore;
                        const awayScore = overrideScore?.away ?? match.awayScore;
                        if (isCompleted || isLive) {
                            return (
                                <span className="score">{homeScore} - {awayScore}</span>
                            );
                        }
                        return <span className="vs">-</span>;
                    })() }
                    <div className="team">
                        <Link to={activeLeague ? (match?.awayTeam?._id === currentTeamId ? buildTeamLink(match?.awayTeam?._id, `/${activeLeague.slug}`) : buildPublicTeamLink(match?.awayTeam?._id, `/${activeLeague.slug}`)) : '#'}>{match?.awayTeam?.name}</Link>
                        <div className="image">
                            <TeamLogo 
                                logoUrl={awayLogoUrl} 
                                teamName={match?.awayTeam?.name}
                            />
                        </div>
                    </div>
                </div>

                <div className="link">
                    <Link to={activeLeague ? `/${activeLeague.slug}/match/${match._id}/lineups` : '#'} className="submit-btn small-btn">
                        Match
                        <span className="ico icon-arrow-right" />
                    </Link>
                </div>
            </div>
        )
    });

    // Display message if loading takes time or no matches found for the date
    if (isLoading) {
        return (
            <div className="matches-container">
                <div className="loader">
                    <Spinner />
                    <p>Loading fixtures for {formattedDateQuery}...</p>
                </div>
            </div>
        );
    }

    if (error) {
        toast.error(`Failed to load fixtures: ${error.message || 'Please try again.'}`);
        return (
            <div className="matches-container">
                <div className="error-message">
                   Failed to load matches for {formattedDateQuery}. Please try changing the date or refresh.
                </div>
            </div>
        );
    }

    // Add a specific message if no matches are found for the selected date
    if (!fixturesData?.matches || fixturesData.matches.length === 0) {
        return (
            <div className="matches-container">
                <div className="empty-message">
                    <img
                        src={ballIcon}
                        alt=""
                        className="empty-icon"
                    />
                    <div className="empty-title">No matches scheduled</div>
                    <div className="empty-subtitle">
                        There are no fixtures for this date. Try selecting a different day from the calendar.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="matches-container">

                <TutorialTooltip
                  id="fixtures-tooltip"
                  title="Fixtures"
                  description="Click on the match to open the match page and watch the game or view additional match information."
                  className="f-bottom"
                />


            {leagueMatches}

            {fixturesData?.pagination && (
                <div className="pagination-controls">
                    <button
                        className="submit-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={!fixturesData.pagination.hasPrevPage}
                    >
                        Previous
                    </button>
                    <span className="page-info">
                        Page {fixturesData.pagination.currentPage} of {fixturesData.pagination.totalPages}
                    </span>
                    <button
                        className="submit-btn"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={!fixturesData.pagination.hasNextPage}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    )
}

export {MatchesBlock}
